import { useState, useMemo } from 'react';
import { FiCopy, FiCheck, FiX } from 'react-icons/fi';
import { useAIPrompt } from '../context/AIPromptContext';

const AIPromptView = () => {
  const { pageMetadata, togglePromptMode } = useAIPrompt();
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);

  // Static JSON - structured data for AI
  const promptJSON = useMemo(() => (pageMetadata.content || {
    page: {
      title: pageMetadata.title,
      description: pageMetadata.description,
      type: pageMetadata.pageType,
      url: pageMetadata.url
    },
    meta: {
      source_url: pageMetadata.url,
      timestamp: new Date().toISOString()
    }
  }), [pageMetadata]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(promptJSON, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black overflow-hidden">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-20 bg-black border-b border-slate-800">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{pageMetadata.title || 'Page Data'}</h1>
            <p className="text-sm text-slate-400 mt-1">JSON prompt for AI assistants</p>
          </div>
          <button
            onClick={togglePromptMode}
            className="p-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white transition-colors"
            aria-label="Close prompt view"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-24 px-6 pb-8 h-full flex flex-col">
        {/* JSON Display - with custom scrollbar */}
        <div className="relative flex-1 overflow-hidden">
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-[#d6fa70] text-black hover:bg-[#c5e960]'
              }`}
            >
              {copied ? (
                <>
                  <FiCheck className="w-5 h-5" />
                  Copied!
                </>
              ) : (
                <>
                  <FiCopy className="w-5 h-5" />
                  Copy JSON
                </>
              )}
            </button>
          </div>

          <pre className="h-full text-slate-100 p-6 font-mono text-sm leading-relaxed overflow-auto scrollbar-custom">
            {JSON.stringify(promptJSON, null, 2)}
          </pre>
        </div>
      </div>

      {/* Instructions Floating Modal - Bottom Right */}
      {showInstructions && (
        <div className="fixed bottom-6 right-6 z-30 max-w-xs">
          <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <span>💡</span>
                  How to Use
                </h2>
                <div className="text-xs text-slate-300 space-y-1">
                  <p>• Copy the JSON and paste into your AI chat</p>
                  <p>• AI will analyze this page data</p>
                </div>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="p-1 text-slate-400 hover:text-white transition-colors flex-shrink-0"
                aria-label="Close instructions"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .scrollbar-custom::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        .scrollbar-custom::-webkit-scrollbar-track {
          background: #0f172a;
          border-radius: 5px;
        }
        .scrollbar-custom::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 5px;
        }
        .scrollbar-custom::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
        .scrollbar-custom {
          scrollbar-width: thin;
          scrollbar-color: #475569 #0f172a;
        }
      `}</style>
    </div>
  );
};

export default AIPromptView;
